import { useState, useEffect, useRef } from 'react';
import style from './Visits.module.scss';
import arrowLeftIcon from '@image/arrow-left-icon.svg';
import arrowRightIcon from '@image/arrow-right-icon.svg';
import Calendar from '@/components/Calendar';
import { format } from 'date-fns';
import { setSelectedDate, useAppDispatch } from '@ax/redux-store';
import { selectedTranscript } from '@/store/selectedTranscript';
import useAnalytics from '@/hooks/useAnalytics';
import { recordingState } from '@/store/recordingState.tsx';

interface VisitDateProps {
  onContinueRecording?: () => void;
  onExecutePendingAction?: () => void;
}

const VisitDate = ({ onContinueRecording, onExecutePendingAction }: VisitDateProps) => {
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedVisitDate, setSelectedVisitDate] = useState<Date>(new Date());
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const formattedCurrentDate = format(selectedVisitDate, 'EEE, MM/dd/yyyy');

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    if (selectedVisitDate) {
      dispatch(setSelectedDate({ date: format(selectedVisitDate, 'yyyy-MM-dd') }));
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDateSelect = (date: Date) => {
    if (recordingState.value.recordingInprogress) {
      setPendingAction(() => () => handleDateSelect(date));
      onContinueRecording?.();
      return;
    }

    if (selectedTranscript.value) {
      selectedTranscript.value = {};
    }

    setSelectedVisitDate(date);
    dispatch(setSelectedDate({ date: format(date, 'yyyy-MM-dd') }));

    analytics.onDatePickerSelection({
      selectedDate: date.toLocaleDateString(),
    });
  };

  const handleSelectPreviousDate = () => {
    if (recordingState.value.recordingInprogress) {
      setPendingAction(() => handleSelectPreviousDate);
      onContinueRecording?.();
      return;
    }

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(new Date().getDate() - 30);

    const previousDate = new Date(selectedVisitDate);
    previousDate.setDate(selectedVisitDate.getDate() - 1);

    if (previousDate >= thirtyDaysAgo) {
      setSelectedVisitDate(previousDate);
      dispatch(setSelectedDate({ date: format(previousDate, 'yyyy-MM-dd') }));
    }

    if (selectedTranscript.value) {
      selectedTranscript.value = {};
    }
  };

  const handleSelectNextDate = () => {
    if (recordingState.value.recordingInprogress) {
      setPendingAction(() => handleSelectNextDate);
      onContinueRecording?.();
      return;
    }

    const maxSelectableDate = new Date();
    maxSelectableDate.setDate(new Date().getDate() + 7);
    const nextDate = new Date(selectedVisitDate);
    nextDate.setDate(selectedVisitDate.getDate() + 1);
    if (nextDate.toISOString() > maxSelectableDate.toISOString()) {
      return;
    }

    setSelectedVisitDate(nextDate);
    dispatch(setSelectedDate({ date: format(nextDate, 'yyyy-MM-dd') }));

    if (selectedTranscript.value) {
      selectedTranscript.value = {};
    }
  };

  useEffect(() => {
    if (!recordingState.value.recordingInprogress && pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [recordingState.value.recordingInprogress]);

  useEffect(() => {
    if (onExecutePendingAction) {
      onExecutePendingAction();
    }
  }, [onExecutePendingAction]);

  return (
    <div ref={componentRef}>
      <ul className={style.visit__date__container}>
        <li
          className={style.visit__date__icon}
          onClick={handleSelectPreviousDate}
        >
          <img
            alt="arrow-left-icon"
            src={arrowLeftIcon}
          />
        </li>
        <li
          className={style.visit__date__label}
          onClick={toggleVisibility}
        >
          {formattedCurrentDate}
        </li>
        <li
          className={style.visit__date__icon}
          onClick={handleSelectNextDate}
        >
          <img
            alt="arrow-right-icon"
            src={arrowRightIcon}
          />
        </li>
      </ul>
      {isVisible && (
        <Calendar
          selectedDate={selectedVisitDate}
          onDateClick={handleDateSelect}
        />
      )}
    </div>
  );
};

export default VisitDate;
